import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useDictation – tale til tekst i nettleseren (Web Speech API, ingen nøkkel, ingen server).
 * Chrome/Edge/Safari (inkl. iOS) støtter det; Firefox gjør det ikke → `supported=false` og UI viser «skriv i stedet».
 *
 *  - `text`     ferdig (final) tekst – kan redigeres av brukeren via `setText`
 *  - `interim`  det nettleseren tror du sier akkurat nå (vises svakt, blir aldri lagret før det er final)
 *  - `status`   idle | listening | error
 *  - iOS stopper gjenkjenningen etter en pause → vi starter automatisk igjen så lenge brukeren ikke har trykket stopp.
 *
 * Safari leverer hele resultatlisten på nytt for hver hendelse (ikke bare det nye), så sesjonsteksten bygges alltid
 * fra hele `event.results` og legges til `base` (tekst fra tidligere sesjoner/redigering).
 */
const getSR = () => (typeof window !== "undefined" ? window.SpeechRecognition || window.webkitSpeechRecognition : null);
export const dictationSupported = () => !!getSR();

const MESSAGES = {
  "not-allowed": "Mikrofonen er blokkert. Gi nettleseren tilgang til mikrofonen og prøv igjen.",
  "service-not-allowed": "Talegjenkjenning er ikke tilgjengelig på denne enheten.",
  "audio-capture": "Fant ingen mikrofon. Koble til en mikrofon og prøv igjen.",
  network: "Talegjenkjenning trenger nett. Sjekk dekningen og prøv igjen.",
  "language-not-supported": "Språket støttes ikke for talegjenkjenning på denne enheten.",
  unsupported: "Nettleseren støtter ikke diktering. Bruk Safari eller Chrome – eller skriv teksten.",
};

const join = (a, b) => {
  const x = (a || "").trim();
  const y = (b || "").trim();
  if (!x) return y;
  if (!y) return x;
  return `${x}${/[.!?…]$/.test(x) ? "" : "."} ${y}`;
};
/** Litt orden på rå tale: stor forbokstav, mellomrom etter tegn, punktum til slutt. */
export const tidy = (s) => {
  let t = (s || "").replace(/\s+/g, " ").trim();
  if (!t) return "";
  t = t.charAt(0).toUpperCase() + t.slice(1);
  if (!/[.!?…]$/.test(t)) t += ".";
  return t;
};

export function useDictation({ lang = "nb-NO" } = {}) {
  const [supported] = useState(() => dictationSupported());
  const [status, setStatus] = useState("idle");
  const [text, setTextState] = useState("");
  const [interim, setInterim] = useState("");
  const [error, setError] = useState(null);
  const recRef = useRef(null);
  const wantRef = useRef(false);
  const baseRef = useRef("");
  const sessionRef = useRef("");
  const startTimer = useRef(null);

  const commitSession = () => {
    if (sessionRef.current) { baseRef.current = join(baseRef.current, tidy(sessionRef.current)); sessionRef.current = ""; }
    setTextState(baseRef.current);
  };

  const stop = useCallback(() => {
    wantRef.current = false;
    if (startTimer.current) clearTimeout(startTimer.current);
    setStatus((s) => (s === "starting" ? "idle" : s));
    try { recRef.current?.stop(); } catch { /* allereie stoppa */ }
  }, []);

  const start = useCallback(() => {
    const SR = getSR();
    if (!SR) { setError({ code: "unsupported", message: MESSAGES.unsupported }); setStatus("error"); return; }
    if (recRef.current) { try { recRef.current.abort(); } catch { /* ignore */ } }
    const rec = new SR();
    rec.lang = lang;
    rec.continuous = true;
    rec.interimResults = true;
    rec.maxAlternatives = 1;
    rec.onstart = () => { if (startTimer.current) clearTimeout(startTimer.current); setStatus("listening"); setError(null); };
    rec.onresult = (e) => {
      let finals = "";
      let live = "";
      for (let i = 0; i < e.results.length; i += 1) {
        const r = e.results[i];
        if (r.isFinal) finals += `${r[0].transcript} `;
        else live += r[0].transcript;
      }
      sessionRef.current = finals;
      setTextState(join(baseRef.current, tidy(finals)));
      setInterim(live.trim());
    };
    rec.onerror = (e) => {
      if (startTimer.current) clearTimeout(startTimer.current);
      if (e.error === "no-speech" || e.error === "aborted") return; // ufarlig – onend tar over
      wantRef.current = false;
      setError({ code: e.error, message: MESSAGES[e.error] || "Diktering feilet. Prøv igjen eller skriv teksten." });
      setStatus("error");
    };
    rec.onend = () => {
      if (startTimer.current) clearTimeout(startTimer.current);
      setInterim("");
      commitSession();
      if (wantRef.current) {
        // iOS/Chrome avslutter etter stillhet – fortsett så lenge brukeren vil
        try { rec.start(); return; } catch { /* fall gjennom */ }
      }
      setStatus((s) => (s === "error" ? s : "idle"));
    };
    recRef.current = rec;
    wantRef.current = true;
    sessionRef.current = "";
    setStatus("starting");
    // Får vi verken onstart eller onerror (f.eks. tillatelsesdialog som aldri blir besvart), sier vi fra i stedet for å henge
    if (startTimer.current) clearTimeout(startTimer.current);
    startTimer.current = setTimeout(() => {
      setStatus((s) => {
        if (s !== "starting") return s;
        wantRef.current = false;
        try { rec.abort(); } catch { /* ignore */ }
        setError({ code: "timeout", message: "Mikrofonen svarte ikke. Sjekk at nettleseren har mikrofontilgang – eller skriv teksten." });
        return "error";
      });
    }, 8000);
    try { rec.start(); } catch (err) {
      wantRef.current = false;
      setError({ code: "start-failed", message: "Kunne ikke starte mikrofonen. Prøv igjen." });
      setStatus("error");
    }
  }, [lang]);

  const toggle = useCallback(() => { if (wantRef.current) stop(); else start(); }, [start, stop]);

  /** Brukeren redigerer teksten selv (eller vi fyller inn utenfra). */
  const setText = useCallback((t) => { baseRef.current = t || ""; sessionRef.current = ""; setTextState(baseRef.current); }, []);
  const reset = useCallback(() => { baseRef.current = ""; sessionRef.current = ""; setTextState(""); setInterim(""); setError(null); }, []);

  useEffect(() => () => { wantRef.current = false; if (startTimer.current) clearTimeout(startTimer.current); try { recRef.current?.abort(); } catch { /* ignore */ } }, []);

  return { supported, status, listening: status === "listening", starting: status === "starting", text, interim, error, start, stop, toggle, setText, reset };
}
